import { useEffect, useState } from 'react'
import { BatteryCharging90, HelpOutline } from '@mui/icons-material'
import { Box } from '@mui/system'
import { Trans, useTranslation } from 'react-i18next'
import { ListSelector } from '../../components/atoms/ListSelector'
import { BatteryAppliance } from './BatteryAppliance'
import { EnergyStorageExtras, EnergyStorageFCRDriver, PortConfig, PortConfigEnergyStorageParams, UncommittedConfig } from '../../types/ports'
import { Text } from '../../components/atoms/Text'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import {
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Slider, Typography,
} from '@mui/material'
import { Button } from '../../components/atoms/Button'
import { Appliance, ApplianceConfigFCRBattery, SaveAbortedError } from './Appliance'
import { CalibrationDialog } from '../../components/CalibrationDialog'
import { driverDefaults } from '../ports/EnergyStoragePortControl'
import { ID, Strategy } from '../../types/models'
import { DeviceControl } from '../DeviceControl'
import { reportClientError } from '../../utils/error-handler'
import { callAxios } from '../../hooks/useAxios'
import { floorToPowerUnit } from '../../utils/batopt-tuning'
import { ReserveTuning } from '../../components/ReserveTuning'

function CalibrationButton({ portId, nominalPower, hwId, userEmail }: { portId: ID, nominalPower: number, hwId: string, userEmail: string }) {
  const [open, setOpen] = useState(false)
  return <>
    <Button onClick={() => setOpen(true)} data-cy="button-Calibration">
      <Trans>Calibration (short)</Trans>
    </Button>
    <CalibrationDialog open={open} onClose={() => setOpen(false)} portId={portId} nominalPower={nominalPower} hwId={hwId} userEmail={userEmail} />
  </>
}

type InverterSpecs = {
  driver: EnergyStorageFCRDriver,
  params: PortConfigEnergyStorageParams
}

type PowerLimits = {
  min_up_power: number,
  max_up_power: number,
  min_down_power: number,
  max_down_power: number,
}

// TODO: Still a bit DRY.
const extras: EnergyStorageExtras = [
  {
    func_code: 0,
    reg_addr: 0,
  },
  {
    func_code: 0,
    reg_addr: 0,
  },
  {
    func_code: 0,
    reg_addr: 0,
  },
  {
    func_code: 0,
    reg_addr: 0,
  },
  {
    func_code: 0,
    reg_addr: 0,
  },
]

const kostalConf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE',
  params: {
    conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP',
    conn_params: {
      tcp_inst: 0,
      keep_socket: 0
    },
    slave_id: 71,
    em_slave_id: 0,
    extras
  }
}

const solis5Conf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_DRIVER_SOLIS_HYBRID',
  params: {
    conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0',
    slave_id: 1,
    em_slave_id: 0,
    extras,
  }
}

const solis6Conf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_DRIVER_SOLIS_S6',
  params: {
    conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0',
    slave_id: 1,
    em_slave_id: 0,
    extras,
  }
}

const sungrowConf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_SUNGROW_HYBRID',
  params: {
    conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0',
    slave_id: 1,
    em_slave_id: 0,
    extras,
  }
}

const kommaConf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_DRIVER_1KOMMA5',
  params: {
    conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0',
    slave_id: 247,
    em_slave_id: 0,
    extras,
  }
}

const growattConf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_DRIVER_GROWATT_HYBRID',
  params: {
    conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0',
    slave_id: 1,
    em_slave_id: 0,
    extras,
  }
}

const dynessConf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_DRIVER_DYNESS_DH200',
  params: {
    conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0',
    slave_id: 1,
    em_slave_id: 0,
    extras,
  }
}

// Huawei MAP0+EMMA: Modbus TCP only (like Kostal), but keep_socket is hard-wired
// to 1. slave_id 0 lets the firmware auto-discover the SUN2000 inverter behind
// the EMMA; em_slave_id is always 0 for the EMMA by definition.
const huaweiMapEmmaConf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_EMMA',
  params: {
    conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP',
    conn_params: {
      tcp_inst: 0,
      keep_socket: 1
    },
    slave_id: 0,
    em_slave_id: 0,
    extras
  }
}

// Solax X3-G4: same energy-storage port conf as the non-FCR battery appliance
// (RS485 port 0 by default, baudrate/parity conn_params shape).
const solaxG4Conf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_G4',
  params: driverDefaults('SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_G4'),
}

// Sigenergy (SigenStor / Hybrid TP2): Modbus TCP with plant-address energy
// meter reads — same conf as the non-FCR battery appliance.
const sigenergyConf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_DRIVER_SIGENERGY',
  params: driverDefaults('SPP_ENERGYSTORAGE_DRIVER_SIGENERGY'),
}

// Sungrow PowerStack: Modbus TCP straight to the LC300 local controller.
const sungrowPowerstackConf: InverterSpecs = {
  driver: 'SPP_ENERGYSTORAGE_DRIVER_SUNGROW_POWERSTACK',
  params: driverDefaults('SPP_ENERGYSTORAGE_DRIVER_SUNGROW_POWERSTACK'),
}

const FCRInverters: Record<string, InverterSpecs> = {
  'Solis S5 6000W': solis5Conf,
  'Solis S5 8000W': solis5Conf,
  'Solis S5 10000W': solis5Conf,
  'Solis S6 10000W': solis6Conf,
  'Solis S6 12000W': solis6Conf,
  'Solis S6 15000W': solis6Conf,
  'Solis S6 20000W': solis6Conf,
  'Solis S6 30000W': solis6Conf,
  'Solis S6 40000W': solis6Conf,
  'Solis S6 50000W': solis6Conf,
  '1komma5 10000W': kommaConf,
  '1komma5 15000W': kommaConf,
  'Growatt MOD10K 10000W': growattConf,
  'Growatt MID20K 20000W': growattConf,
  'Kostal Plenticore BI 5500W': kostalConf,
  'Kostal Plenticore G3 S 4000W': kostalConf,
  'Kostal Plenticore G3 S 5500W': kostalConf,
  'Kostal Plenticore G3 S 7000W': kostalConf,
  'Kostal Plenticore G3 M 8500W': kostalConf,
  'Kostal Plenticore G3 M 10000W': kostalConf,
  'Kostal Plenticore G3 M 12500W': kostalConf,
  'Kostal Plenticore G3 L 15000W': kostalConf,
  'Kostal Plenticore G3 L 17500W': kostalConf,
  'Kostal Plenticore G3 L 20000W': kostalConf,
  'Kostal Plenticore Plus 4200W': kostalConf,
  'Kostal Plenticore Plus 7000W': kostalConf,
  'Kostal Plenticore Plus 8500W': kostalConf,
  'Kostal Plenticore Plus 10000W': kostalConf,
  'Dyness DH200 100000W': dynessConf,
  'Sungrow 6000W': sungrowConf,
  'Sungrow 10000W': sungrowConf,
  'Huawei MAP0+EMMA 5000W': huaweiMapEmmaConf,
  'Huawei MAP0+EMMA 6000W': huaweiMapEmmaConf,
  'Huawei MAP0+EMMA 8000W': huaweiMapEmmaConf,
  'Huawei MAP0+EMMA 10000W': huaweiMapEmmaConf,
  'Huawei MAP0+EMMA 12000W': huaweiMapEmmaConf,
  'Solax X3-G4 12000W': solaxG4Conf,
  'Sigenergy SigenStor 10000W': sigenergyConf,
  'Sigenergy TP2 6000W': sigenergyConf,
  'Sigenergy TP2 8000W': sigenergyConf,
  'Sigenergy TP2 10000W': sigenergyConf,
  'Sigenergy TP2 12000W': sigenergyConf,
  'Sungrow PowerStack 200LC 125000W': sungrowPowerstackConf,
}

const FCRPowerLimits: Record<string, PowerLimits | null> = {
  'Solis S5 6000W': {
    min_up_power: 2000,
    max_up_power: 5000,
    min_down_power: 2000,
    max_down_power: 5000,
  },
  'Solis S5 8000W': {
    min_up_power: 2000,
    max_up_power: 7000,
    min_down_power: 2000,
    max_down_power: 7000,
  },
  'Solis S5 10000W': {
    min_up_power: 3000,
    max_up_power: 9000,
    min_down_power: 3000,
    max_down_power: 9000,
  },
  'Solis S6 10000W': {
    min_up_power: 3000,
    max_up_power: 9000,
    min_down_power: 3000,
    max_down_power: 9000,
  },
  'Solis S6 12000W': {
    min_up_power: 3000,
    max_up_power: 11000,
    min_down_power: 3000,
    max_down_power: 11000,
  },
  'Solis S6 15000W': {
    min_up_power: 3000,
    max_up_power: 13000,
    min_down_power: 3000,
    max_down_power: 14000,
  },
  'Solis S6 20000W': {
    min_up_power: 4000,
    max_up_power: 18000,
    min_down_power: 4000,
    max_down_power: 19000,
  },
  'Solis S6 30000W': {
    min_up_power: 8000,
    max_up_power: 36000,
    min_down_power: 8000,
    max_down_power: 40000,
  },
  'Solis S6 40000W': {
    min_up_power: 8000,
    max_up_power: 36000,
    min_down_power: 8000,
    max_down_power: 40000,
  },
  'Solis S6 50000W': {
    min_up_power: 8000,
    max_up_power: 48000,
    min_down_power: 8000,
    max_down_power: 50000,
  },
  '1komma5 10000W': {
    min_up_power: 3000,
    max_up_power: 9000,
    min_down_power: 3000,
    max_down_power: 9000,
  },
  '1komma5 15000W': {
    min_up_power: 4000,
    max_up_power: 14000,
    min_down_power: 4000,
    max_down_power: 14000,
  },
  'Growatt MOD10K 10000W': {
    min_up_power: 3000,
    max_up_power: 10000,
    min_down_power: 3000,
    max_down_power: 9000,
  },
  'Growatt MID20K 20000W': {
    min_up_power: 8000,
    max_up_power: 19000,
    min_down_power: 8000,
    max_down_power: 19000,
  },
  'Kostal Plenticore BI 5500W': {
    min_up_power: 2000,
    max_up_power: 4000,
    min_down_power: 2000,
    max_down_power: 5000,
  },
  'Kostal Plenticore G3 S 4000W': {
    min_up_power: 2000,
    max_up_power: 7000,
    min_down_power: 2000,
    max_down_power: 7000,
  },
  'Kostal Plenticore G3 S 5500W': {
    min_up_power: 2000,
    max_up_power: 7000,
    min_down_power: 2000,
    max_down_power: 7000,
  },
  'Kostal Plenticore G3 S 7000W': {
    min_up_power: 2000,
    max_up_power: 7000,
    min_down_power: 2000,
    max_down_power: 7000,
  },
  'Kostal Plenticore G3 M 8500W': {
    min_up_power: 2000,
    max_up_power: 12000,
    min_down_power: 2000,
    max_down_power: 12000,
  },
  'Kostal Plenticore G3 M 10000W': {
    min_up_power: 2000,
    max_up_power: 12000,
    min_down_power: 2000,
    max_down_power: 12000,
  },
  'Kostal Plenticore G3 M 12500W': {
    min_up_power: 2000,
    max_up_power: 12000,
    min_down_power: 2000,
    max_down_power: 12000,
  },
  'Kostal Plenticore G3 L 15000W': {
    min_up_power: 3000,
    max_up_power: 18000,
    min_down_power: 3000,
    max_down_power: 18000,
  },
  'Kostal Plenticore G3 L 17500W': {
    min_up_power: 3000,
    max_up_power: 18000,
    min_down_power: 3000,
    max_down_power: 18000,
  },
  'Kostal Plenticore G3 L 20000W': {
    min_up_power: 3000,
    max_up_power: 18000,
    min_down_power: 3000,
    max_down_power: 18000,
  },
  'Kostal Plenticore Plus 4200W': {
    min_up_power: 2000,
    max_up_power: 4000,
    min_down_power: 2000,
    max_down_power: 4000,
  },
  'Kostal Plenticore Plus 7000W': {
    min_up_power: 2000,
    max_up_power: 7000,
    min_down_power: 2000,
    max_down_power: 7000,
  },
  'Kostal Plenticore Plus 8500W': {
    min_up_power: 2000,
    max_up_power: 7000,
    min_down_power: 2000,
    max_down_power: 7000,
  },
  'Kostal Plenticore Plus 10000W': {
    min_up_power: 2000,
    max_up_power: 7000,
    min_down_power: 2000,
    max_down_power: 7000,
  },
  'Dyness DH200 100000W': {
    min_up_power: 20000,
    max_up_power: 99000,
    min_down_power: 20000,
    max_down_power: 99000,
  },
  'Sungrow 6000W': {
    min_up_power: 2000,
    max_up_power: 4000,
    min_down_power: 2000,
    max_down_power: 5000,
  },
  'Sungrow 10000W': {
    min_up_power: 3000,
    max_up_power: 9000,
    min_down_power: 3000,
    max_down_power: 9000,
  },
  'Huawei MAP0+EMMA 5000W': {
    min_up_power: 2000,
    max_up_power: 5000,
    min_down_power: 2000,
    max_down_power: 5000,
  },
  'Huawei MAP0+EMMA 6000W': {
    min_up_power: 2000,
    max_up_power: 6000,
    min_down_power: 2000,
    max_down_power: 6000,
  },
  'Huawei MAP0+EMMA 8000W': {
    min_up_power: 2000,
    max_up_power: 8000,
    min_down_power: 2000,
    max_down_power: 8000,
  },
  'Huawei MAP0+EMMA 10000W': {
    min_up_power: 2000,
    max_up_power: 10000,
    min_down_power: 2000,
    max_down_power: 10000,
  },
  'Huawei MAP0+EMMA 12000W': {
    min_up_power: 2000,
    max_up_power: 10000,
    min_down_power: 2000,
    max_down_power: 10000,
  },
  'Solax X3-G4 12000W': {
    min_up_power: 3000,
    max_up_power: 11000,
    min_down_power: 3000,
    max_down_power: 9000,
  },
  'Sigenergy SigenStor 10000W': {
    min_up_power: 2000,
    max_up_power: 8000,
    min_down_power: 2000,
    max_down_power: 8000,
  },
  // Sigenergy TP2: qualified up to 11 kW, capped to nominal power below the 12 kW model.
  'Sigenergy TP2 6000W': {
    min_up_power: 2000,
    max_up_power: 6000,
    min_down_power: 2000,
    max_down_power: 6000,
  },
  'Sigenergy TP2 8000W': {
    min_up_power: 2000,
    max_up_power: 8000,
    min_down_power: 2000,
    max_down_power: 8000,
  },
  'Sigenergy TP2 10000W': {
    min_up_power: 2000,
    max_up_power: 10000,
    min_down_power: 2000,
    max_down_power: 10000,
  },
  'Sigenergy TP2 12000W': {
    min_up_power: 2000,
    max_up_power: 11000,
    min_down_power: 2000,
    max_down_power: 11000,
  },
  'Sungrow PowerStack 200LC 125000W': {
    min_up_power: 20000,
    max_up_power: 100000,
    min_down_power: 20000,
    max_down_power: 100000,
  },
}

function MaxReservePowersSection({ appliance, inverterModel }: { appliance: FCRBatteryAppliance, inverterModel: string }) {
  const { t } = useTranslation()
  const canonical = FCRPowerLimits[inverterModel]

  const currentMaxUp = ((appliance.getConfig('inverter_max_up_power') as number | null) ?? canonical?.max_up_power ?? 0)
  const currentMaxDown = ((appliance.getConfig('inverter_max_down_power') as number | null) ?? canonical?.max_down_power ?? 0)

  const [limitEnabled, setLimitEnabled] = useState(
    !!canonical && (currentMaxUp < canonical.max_up_power || currentMaxDown < canonical.max_down_power)
  )
  const [helpOpen, setHelpOpen] = useState(false)

  // Reset toggle state when inverter changes (canonical bounds change).
  useEffect(() => {
    if (!canonical) {
      setLimitEnabled(false)
      return
    }
    setLimitEnabled(currentMaxUp < canonical.max_up_power || currentMaxDown < canonical.max_down_power)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inverterModel])

  if (!canonical) return null

  const onToggle = () => {
    if (limitEnabled) {
      // Reset all four bounds to canonical when disabling.
      appliance.setConfig('inverter_min_up_power', canonical.min_up_power, false)
      appliance.setConfig('inverter_max_up_power', canonical.max_up_power, false)
      appliance.setConfig('inverter_min_down_power', canonical.min_down_power, false)
      appliance.setConfig('inverter_max_down_power', canonical.max_down_power)
      setLimitEnabled(false)
    } else {
      // Ensure mins are at canonical mins when enabling.
      appliance.setConfig('inverter_min_up_power', canonical.min_up_power, false)
      appliance.setConfig('inverter_min_down_power', canonical.min_down_power)
      setLimitEnabled(true)
    }
  }

  const fmt = (v: number) => `${v} W`

  return <Box sx={{ mt: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <CustomSwitch
        data-cy="switch-Limit Max Reserve Powers"
        onClick={onToggle}
        checked={limitEnabled}
      />
      <Typography component="span" sx={{ ml: 2 }}>
        <Trans>Limit maximum reserve powers</Trans>
      </Typography>
      <IconButton
        size="small"
        aria-label={t('Help')}
        onClick={() => setHelpOpen(true)}
        sx={{ ml: 1 }}
      >
        <HelpOutline fontSize="small" />
      </IconButton>
    </Box>

    {limitEnabled && <Box sx={{ mt: 1 }}>
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography>{t('FCR-D up regulation (battery discharge), reserve power limit')}</Typography>
        <Slider
          data-cy="slider-Max Up Power"
          size="medium"
          sx={{ mt: 5, width: '80%' }}
          value={currentMaxUp}
          min={canonical.min_up_power}
          max={canonical.max_up_power}
          step={100}
          marks={[
            { value: canonical.min_up_power, label: <Box sx={{ color: '#A3A3A3' }}>{fmt(canonical.min_up_power)}</Box> },
            { value: canonical.max_up_power, label: <Box sx={{ color: '#A3A3A3' }}>{fmt(canonical.max_up_power)}</Box> },
          ]}
          valueLabelFormat={fmt}
          valueLabelDisplay="on"
          onChange={(_, v) => appliance.setConfig('inverter_max_up_power', v as number)}
        />
      </Box>
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography>{t('FCR-D down regulation (battery charge), reserve power limit')}</Typography>
        <Slider
          data-cy="slider-Max Down Power"
          size="medium"
          sx={{ mt: 5, width: '80%' }}
          value={currentMaxDown}
          min={canonical.min_down_power}
          max={canonical.max_down_power}
          step={100}
          marks={[
            { value: canonical.min_down_power, label: <Box sx={{ color: '#A3A3A3' }}>{fmt(canonical.min_down_power)}</Box> },
            { value: canonical.max_down_power, label: <Box sx={{ color: '#A3A3A3' }}>{fmt(canonical.max_down_power)}</Box> },
          ]}
          valueLabelFormat={fmt}
          valueLabelDisplay="on"
          onChange={(_, v) => appliance.setConfig('inverter_max_down_power', v as number)}
        />
      </Box>
    </Box>}

    <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle><Trans>Limit maximum reserve powers</Trans></DialogTitle>
      <DialogContent dividers>
        <Typography>{t('Limit max reserve powers help')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setHelpOpen(false)}><Trans>Close</Trans></Button>
      </DialogActions>
    </Dialog>
  </Box>
}

export class FCRBatteryAppliance extends BatteryAppliance {

  /**
   * Strategy the appliance had when the configuration page opened. The
   * reserve-participation calls only run on an actual transition: firing
   * "return to reserve" on every save would undo a pause an FCR operator set
   * deliberately.
   */
  loadedStrategy?: Strategy

  hasSocControls() {
    return false
  }

  createHook(): void {
    this.loadedStrategy = this.getStrategy()
  }

  manualModeWarnings(): string[] {
    return [
      ...super.manualModeWarnings(),
      this.app.t('Switching to this mode temporarily removes your battery from the reserve.'),
    ]
  }

  saveConfirmation(): string | null {
    if (!this.isManualControl() || this.loadedStrategy === 'BATTERY_MANUAL') {
      return null
    }

    return this.app.t('Are you sure? Switching to manual command removes the equipment from the reserve. Returning it to the reserve can take 1-2 days.')
  }

  /**
   * Leave the reserve before the optimizer is switched off.
   *
   * Order matters here. If the release fails and we had already flashed, the
   * battery would be physically unable to deliver while FCR still counted it —
   * the one state that costs real money. Doing it first means a failure leaves
   * the battery exactly as it was: in the reserve, still optimizing. The save
   * is aborted so nothing is flashed.
   */
  async beforeFlash(): Promise<void> {
    if (!this.isEnteringManualControl()) return

    if (!await this.setReserveParticipation(false)) {
      throw new SaveAbortedError(`Could not remove FCR battery appliance ${this.id} from the reserve; flash aborted.`)
    }
  }

  /**
   * Return to the reserve once the optimizer is back on.
   *
   * The mirror of `beforeFlash`: lift the pause only after the device has
   * actually been told to optimize again, never before. A failure here leaves
   * the unit paused, which is the harmless direction — the battery optimizes
   * for its owner and simply earns no reserve income until someone retries.
   */
  async afterSave(): Promise<void> {
    if (!this.isLeavingManualControl()) return

    await this.setReserveParticipation(true)
  }

  private isEnteringManualControl(): boolean {
    return this.isManualControl() && this.loadedStrategy !== 'BATTERY_MANUAL'
  }

  private isLeavingManualControl(): boolean {
    return !this.isManualControl() && this.loadedStrategy === 'BATTERY_MANUAL'
  }

  /**
   * Take the battery out of the reserve or put it back.
   *
   * Leaving pauses the unit's bidding and drops its constraints from the start
   * of the running hour onward; returning only lifts the pause, and the unit
   * re-enters through the normal bidding cycle. Both are scoped server-side to
   * this one port of this one device.
   */
  private async setReserveParticipation(participating: boolean): Promise<boolean> {
    const portId = this.getBatteryControl().port.id as ID

    const res = await callAxios({
      method: participating ? 'POST' : 'DELETE',
      url: `/constraints/${portId}`,
    })

    if (res.success) {
      this.loadedStrategy = this.getStrategy()
    } else {
      this.app.addError(res)
    }

    return res.success
  }

  canContinue() {
    return !!this.getConfig('inverter') && !!this.getConfig('battery')
  }

  getDefaultConfig(): ApplianceConfigFCRBattery {
    return {
      ...super.getDefaultConfig(),
      comments: '',
      capacity: 0,
      power: 0,
      enabled: false,
      driver: 'SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE',
      params: driverDefaults('SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE'),
      solar_connected: false,
      solar_connected_power: 0,
      solar_onprem: false,
      solar_onprem_power: 0,
      inverter_min_up_power: null,
      inverter_max_up_power: null,
      inverter_min_down_power: null,
      inverter_max_down_power: null,
    }
  }

  prepareSave(): void {
    const ctrl = this.getControl(0)
    const ctrlParams = ctrl?.getConfig('params') as PortConfigEnergyStorageParams | undefined
    if (ctrlParams) {
      const conf = this.getConfig('params') as PortConfigEnergyStorageParams
      this.setConfig('params', { ...conf, ...ctrlParams }, false)
    }
  }

  buildFlashPayload(): UncommittedConfig[] {
    this.prepareSave()
    const ctrl = this.getControl(0)

    // The appliance config is the source of truth for the energy-storage port.
    // If driver/params are absent it means the appliance config was lost or
    // corrupted (a known historical bug). Refuse to flash rather than silently
    // substituting the meaningless Kostal/Modbus-TCP default, which would
    // mis-flash non-Kostal inverters. Surface it to Slack via the exceptions
    // endpoint so the data loss is caught immediately instead of months later.
    const driver = this.getConfig('driver')
    const params = this.getConfig('params')
    if (!driver || !params) {
      reportClientError(
        `FCR battery flash aborted: appliance ${this.id} is missing ` +
        `${!driver ? 'driver' : 'params'} (inverter=${this.getConfig('inverter') ?? 'none'}).`
      )
      this.app.addError(this.app.t('Configuration incomplete — please re-select the inverter model and save again.'))
      throw new SaveAbortedError(`FCR battery appliance ${this.id} config incomplete; flash aborted.`)
    }

    const items: UncommittedConfig[] = [{
      id: ctrl.port.id as ID,
      port: ctrl.port.port_number,
      config: {
        // An existing FCR battery appliance always keeps its energy-storage port
        // enabled; disabling on delete is handled separately in onDelete().
        enabled: true,
        to_summer: ctrl.getConfig('to_summer') ?? false,
        driver,
        params,
      } as PortConfig,
    }]

    // Include Battery Optimizer port (ctrl 1) if it exists and is connected.
    if (this.controls.length > 1) {
      const optimizerCtrl = this.getOptimizerControl()
      items.push(DeviceControl.buildPortFlashItem(optimizerCtrl))
    }

    return items
  }

  async saveConfig(): Promise<void> {
    this.prepareSave()
    await super.saveConfig()
  }

  applyConfig(): void {
    const ctrl = this.getControl(0)
    const conf = { ...this.config, ...(ctrl.getPendingConfig() || {}) } as ApplianceConfigFCRBattery
    const driver = conf.driver || 'SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE'

    ctrl.setConfig('enabled', conf.enabled)
    ctrl.setConfig('driver', driver)
    ctrl.setConfig('params', { ...driverDefaults(driver), ...conf.params })
  }

  /** Nominal power of the selected inverter model (`power`, parsed from the model name). */
  selfUseMaxPowerW(): number | null {
    return Number(this.getConfig('power')) || null
  }

  /** The bid price factor the FCR service applies to the unit's bids (unset = 1.00). */
  reserveTuning(): React.ReactNode {
    return <ReserveTuning
      factor={this.getConfig('bid_price_factor')}
      onChange={(factor) => this.setConfig('bid_price_factor', factor)}
    />
  }

  extraSettingsButtons(): React.ReactNode {
    const ctrl = this.getBatteryControl()
    const portId = ctrl.port.id as ID
    const nominalPower = (this.getConfig('power') as number) || 0
    const device = ctrl.deviceControl.device
    const hwId = device.hw_id
    const userEmail = device.owner?.email || device.user?.email || ''

    return <CalibrationButton portId={portId} nominalPower={nominalPower} hwId={hwId} userEmail={userEmail} />
  }

  configTop(appliances: Appliance[]) : React.ReactNode | undefined {
    const comments = this.getConfig('comments')
    if (!comments && comments !== '') {
      this.setConfig('comments', '')
    }

    return <Box>
      <Box>
        <Trans>Comments</Trans>
        <Box>
          <Text
            fullWidth
            size="small"
            sx={{ my: 1.5, height: 0.8 }}
            defaultValue={this.getConfig('comments') || ''}
            onChange={(e) => this.setConfig('comments', e.target.value)}
          />
        </Box>
      </Box>
      { super.configTop(appliances) }
    </Box>
  }

  /**
   * Select the inverter model: driver and conn params, reserve power bounds
   * and the self-use power limits all follow from it.
   */
  selectInverter(model: string): void {
    const ctrl = this.getBatteryControl()
    const match = /(\d+)W$/.exec(model)
    const power = match ? parseInt(match[1]) : 0
    this.setConfig('power', power)
    this.setConfig('inverter', model)

    ctrl.setConfig('enabled', true)
    ctrl.setConfig('driver', FCRInverters[model].driver)
    ctrl.setConfig('params', FCRInverters[model].params)

    // Set appliance config values, to save to local storage
    this.setConfig('enabled', true)
    this.setConfig('driver', FCRInverters[model].driver)
    this.setConfig('params', FCRInverters[model].params)

    this.setConfig('inverter_min_up_power', (FCRPowerLimits[model] as PowerLimits).min_up_power || null)
    this.setConfig('inverter_max_up_power', (FCRPowerLimits[model] as PowerLimits).max_up_power || null)
    this.setConfig('inverter_min_down_power', (FCRPowerLimits[model] as PowerLimits).min_down_power || null)
    this.setConfig('inverter_max_down_power', (FCRPowerLimits[model] as PowerLimits).max_down_power || null)

    // Self-use power limits default to the inverter's nominal power, and
    // follow it when the model changes. Reserve power is not affected.
    if (power && this.controls.length > 1) {
      const optimizerCtrl = this.getOptimizerControl()
      optimizerCtrl.setConfig('max_charge_power', floorToPowerUnit(power), false)
      optimizerCtrl.setConfig('max_discharge_power', floorToPowerUnit(power))
    }
  }

  configTopLeft() : React.ReactNode | undefined {

    const ctrl = this.getBatteryControl()
    const inverter = this.getConfig('inverter') as string || null
    const battery = this.getConfig('battery') as string || null

    const onChangeBattery = (model: string) => {
      this.setConfig('battery', model)
      ctrl.setConfig('enabled', true)
      this.setConfig('enabled', true)
    }

    const onBlurCapacity = () => {
      let power = parseInt(this.getConfig('capacity') || '0')
      if (power < 100) {
        power *= 1000
      }
      this.setConfig('capacity', power)
    }

    return (
      <Box>
        <Trans>Inverter Model</Trans>

        <ListSelector
          items={{
            'Solis S5 6000W': 'Solis S5 6kW',
            'Solis S5 8000W': 'Solis S5 8kW',
            'Solis S5 10000W': 'Solis S5 10kW',
            'Solis S6 10000W': 'Solis S6 10kW',
            'Solis S6 12000W': 'Solis S6 12kW',
            'Solis S6 15000W': 'Solis S6 15kW',
            'Solis S6 20000W': 'Solis S6 20kW',
            'Solis S6 30000W': 'Solis S6 30kW',
            'Solis S6 40000W': 'Solis S6 40kW',
            'Solis S6 50000W': 'Solis S6 50kW',
            '1komma5 10000W': '1komma5 10kW',
            '1komma5 15000W': '1komma5 15kW',
            'Growatt MOD10K 10000W': 'Growatt MOD10K 10kW',
            'Growatt MID20K 20000W': 'Growatt MID20K 20kW',
            'Kostal Plenticore BI 5500W': 'Kostal Plenticore BI 5.5kW',
            'Kostal Plenticore G3 S 4000W': 'Kostal Plenticore G3 S 4.0kW',
            'Kostal Plenticore G3 S 5500W': 'Kostal Plenticore G3 S 5.5kW',
            'Kostal Plenticore G3 S 7000W': 'Kostal Plenticore G3 S 7.0kW',
            'Kostal Plenticore G3 M 8500W': 'Kostal Plenticore G3 M 8.5kW',
            'Kostal Plenticore G3 M 10000W': 'Kostal Plenticore G3 M 10kW',
            'Kostal Plenticore G3 M 12500W': 'Kostal Plenticore G3 M 12.5kW',
            'Kostal Plenticore G3 L 15000W': 'Kostal Plenticore G3 L 15kW',
            'Kostal Plenticore G3 L 17500W': 'Kostal Plenticore G3 L 17.5kW',
            'Kostal Plenticore G3 L 20000W': 'Kostal Plenticore G3 L 20kW',
            'Kostal Plenticore Plus 4200W': 'Kostal Plenticore Plus 4.2kW',
            'Kostal Plenticore Plus 7000W': 'Kostal Plenticore Plus 7kW',
            'Kostal Plenticore Plus 8500W': 'Kostal Plenticore Plus 8.5kW',
            'Kostal Plenticore Plus 10000W': 'Kostal Plenticore Plus 10kW',
            'Dyness DH200 100000W': 'Dyness DH200',
            'Sungrow 6000W': 'Sungrow 6kW',
            'Sungrow 10000W': 'Sungrow 10kW',
            'Huawei MAP0+EMMA 5000W': 'Huawei MAP0+EMMA 5kW',
            'Huawei MAP0+EMMA 6000W': 'Huawei MAP0+EMMA 6kW',
            'Huawei MAP0+EMMA 8000W': 'Huawei MAP0+EMMA 8kW',
            'Huawei MAP0+EMMA 10000W': 'Huawei MAP0+EMMA 10kW',
            'Huawei MAP0+EMMA 12000W': 'Huawei MAP0+EMMA 12kW',
            'Solax X3-G4 12000W': 'Solax X3-G4 12kW',
            'Sigenergy SigenStor 10000W': 'Sigenergy SigenStor 10kW',
            'Sigenergy TP2 6000W': 'Sigenergy TP2 6kW',
            'Sigenergy TP2 8000W': 'Sigenergy TP2 8kW',
            'Sigenergy TP2 10000W': 'Sigenergy TP2 10kW',
            'Sigenergy TP2 12000W': 'Sigenergy TP2 12kW',
            'Sungrow PowerStack 200LC 125000W': 'Sungrow PowerStack 200LC 125kW',
          }}
          icons={[BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90]}
          selected={inverter}
          onSelect={(model: string) => this.selectInverter(model)}
        />

        <Text
          sx={{ pr: 1.5, mt: 1.5 }}
          label={this.app.t('Battery Model')}
          defaultValue={battery}
          onChange={(e) => onChangeBattery(e.target.value)}
        />

        <Text
          sx={{ pr: 1.5, mt: 2.5 }}
          label={this.app.t('Battery Capacity') + ' (Wh)'}
          type="number"
          positive
          integer
          value={this.getConfig('capacity')}
          onChange={(e) => this.setConfig('capacity', e.target.value)}
          onBlur={onBlurCapacity}
        />

        {inverter && FCRPowerLimits[inverter] && (
          <MaxReservePowersSection key={inverter} appliance={this} inverterModel={inverter} />
        )}

      </Box>
    )
  }

  configTopRight() : React.ReactNode | undefined {

    const onToggleSolarConnected = () => {
      const solarConnected = !this.getConfig('solar_connected')
      this.setConfig('solar_connected', solarConnected)
      if (solarConnected) {
        this.setConfig('has_solar', true)
      }
    }

    const onBlurSolarOnprem = () => {
      let power = parseInt(this.getConfig('solar_onprem_power') || '0')
      if (power < 100) {
        power *= 1000
      }
      this.setConfig('solar_onprem_power', power)
    }

    const onBlurSolarConnected = () => {
      let power = parseInt(this.getConfig('solar_connected_power') || '0')
      if (power < 100) {
        power *= 1000
      }
      this.setConfig('solar_connected_power', power)
    }

    return <Box>
      {super.configTopRight()}
      <Box>
        <Trans>Solar Panels</Trans>

        <Box sx={{ display: 'flex' }}>
            <CustomSwitch data-cy="switch-Solar Connected" onClick={onToggleSolarConnected} checked={!!this.getConfig('solar_connected')}/>
            <Typography component="span" sx={{ ml: 2 }}>
              <Trans>Solar panels connected to the reserve inverter</Trans>
            </Typography>
        </Box>

        <Text
          sx={{ pr: 1.5, mt: 2.5, mb: 1, display: this.getConfig('solar_connected') ? 'block' : 'none' }}
          label={this.app.t('Nominal PV power total') + ' (Wp)'}
          type="number"
          positive
          integer
          value={this.getConfig('solar_connected_power')}
          onChange={(e) => this.setConfig('solar_connected_power', e.target.value)}
          onBlur={onBlurSolarConnected}
        />

        <Box sx={{ display: 'flex' }}>
            <CustomSwitch data-cy="switch-To Summer" onClick={() => this.setConfig('solar_onprem', !this.getConfig('solar_onprem'))} checked={!!this.getConfig('solar_onprem')}/>
            <Typography component="span" sx={{ ml: 2 }}>
              <Trans>Separate PV inverter within the same premises</Trans>
            </Typography>
        </Box>

        <Text
          sx={{ pr: 1.5, mt: 2.5, mb: 1, display: this.getConfig('solar_onprem') ? 'block' : 'none' }}
          label={this.app.t('Nominal PV power total') + ' (Wp)'}
          type="number"
          positive
          integer
          value={this.getConfig('solar_onprem_power')}
          onChange={(e) => this.setConfig('solar_onprem_power', e.target.value)}
          onBlur={onBlurSolarOnprem}
        />

      </Box>
    </Box>
  }
}
